import React, {useEffect, useMemo, useState} from 'react';
import {Card, Col, message, Row, Select, Skeleton, Space, Table} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {MinusSquareOutlined, PlusSquareOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {useTranslation} from '../../../../hooks/useTranslation';
import {tokens} from '../../../../theme/tokens';
import {useIssueDetail} from '../../../../hooks/issues/useIssues';
import {type ScaleBand, useScaleBands} from '../../../../hooks/useScaleBands';
import {useVariablesGroup} from '../../../../hooks/parameters';
import {
  type AssessmentOption,
  type AssessmentVariable,
  type AssessmentVariableGroup,
  useGroupOptionsBatch,
  useIssueAssessmentVariables,
  useSaveIssueAssessment,
  useVariableOptionsBatch,
} from '../../../../hooks/issues/useIssueAssessment';
import {evalFormula} from '../../../../utils/formulaEngine';
import ScoreMeter from '../../../../components/common/ScoreMeter';
import IssueSummaryCard from '../components/IssueSummaryCard';
import type {IssueFormActions} from './IssueForm';
import {extractErrorMessage} from '../../../../utils/apiError';

interface Props {
  issueId?: string;
  issueCorrelative?: number;
  activePanel: string;
  onRegisterActions?: (actions: IssueFormActions) => void;
  onSavingChange?: (saving: boolean) => void;
}

const labelOf = (group: AssessmentVariableGroup): string =>
  group.descriptionGroup || group.description || group.name || `#${group.id}`;

const variableLabelOf = (variable: AssessmentVariable): string =>
  variable.description || variable.name || `#${variable.id}`;

const numberOf = (value: number | null | undefined): number =>
  value == null ? 0 : Number(value);

const matchBand = (bands: ScaleBand[], score: number): ScaleBand | null => {
  if (!bands.length) return null;
  return bands.find((b) => score >= numberOf(b.scoreMin) && score <= numberOf(b.scoreMax)) ?? null;
};

const optionDotColor = (option: AssessmentOption | undefined, bands: ScaleBand[]): string => {
  if (!option) return tokens.colors.borderDefault;
  if (option.scaleBandColor) return option.scaleBandColor;
  if (option.scaleBandId != null) {
    const band = bands.find((b) => Number(b.id) === Number(option.scaleBandId));
    if (band?.color) return band.color;
  }
  return tokens.colors.primary;
};

const SeverityDot: React.FC<{ color: string }> = ({color}) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 12,
      borderRadius: '20%',
      background: color,
      flexShrink: 0,
    }}
  />
);

interface VariableRow {
  key: string;
  isGroup: false;
  variable: AssessmentVariable;
  options: AssessmentOption[];
  groupId: number;
  hidden: boolean;
  selectedOptionId: number | undefined;
  optionsLoading: boolean;
}

interface GroupRow {
  key: string;
  isGroup: true;
  group: AssessmentVariableGroup;
  worstOption: AssessmentOption | null;
  expanded: boolean;
}

type Row = VariableRow | GroupRow;

const ImpactAssessment: React.FC<Props> = ({issueId, issueCorrelative, onRegisterActions, onSavingChange}) => {
  const {t} = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  const isEditMode = !!issueId && issueId !== 'new';

  const {data: groupsTree = [], isLoading: isLoadingTree} = useVariablesGroup() as {
    data: AssessmentVariableGroup[];
    isLoading: boolean;
  };
  const {data: scaleBands = []} = useScaleBands();
  const {data: issueData} = useIssueDetail(isEditMode ? issueId : undefined);

  // Assessment endpoints key on the numeric correlative; the UUID is only for the detail GET / invalidation.
  const {data: savedVariables = []} = useIssueAssessmentVariables(issueCorrelative);
  const saveAssessment = useSaveIssueAssessment(issueId);

  const variableIds = useMemo(
    () => groupsTree.flatMap((g) => (g.children ?? []).map((v) => Number(v.id))),
    [groupsTree]
  );

  const [openedVariableIds, setOpenedVariableIds] = useState<Set<number>>(new Set());

  const [selections, setSelections] = useState<Record<number, number | undefined>>({});

  const activeVariableIds = useMemo(() => {
    const set = new Set<number>(openedVariableIds);
    variableIds.forEach((id) => {
      if (selections[id] != null) set.add(id);
    });
    return set;
  }, [openedVariableIds, variableIds, selections]);

  const optionQueries = useVariableOptionsBatch(variableIds, activeVariableIds);

  const optionsByVariableId = useMemo(() => {
    const map = new Map<number, AssessmentOption[]>();
    variableIds.forEach((id, idx) => {
      const data = optionQueries[idx]?.data;
      if (data) map.set(id, data);
    });
    return map;
  }, [variableIds, optionQueries]);

  const loadingByVariableId = useMemo(() => {
    const map = new Map<number, boolean>();
    variableIds.forEach((id, idx) => {
      map.set(Number(id), optionQueries[idx]?.isLoading);
    });
    return map;
  }, [variableIds, optionQueries]);

  const isLoadingActiveOptions = useMemo(
    () => variableIds.some((id, idx) => selections[Number(id)] != null && optionQueries[idx]?.isLoading),
    [variableIds, selections, optionQueries]
  );

  const handleOpenVariable = (variableId: number) => {
    setOpenedVariableIds((prev) => {
      if (prev.has(variableId)) return prev;
      const next = new Set(prev);
      next.add(variableId);
      return next;
    });
  };

  const groupIds = useMemo(() => groupsTree.map((g) => Number(g.id)), [groupsTree]);
  const groupOptionQueries = useGroupOptionsBatch(groupIds);
  const groupOptionsByGroupId = useMemo(() => {
    const map = new Map<number, AssessmentOption[]>();
    groupIds.forEach((id, idx) => {
      const data = groupOptionQueries[idx]?.data;
      if (data) map.set(id, data);
    });
    return map;
  }, [groupIds, groupOptionQueries]);
  const isLoadingGroupOptions = groupOptionQueries.some((q) => q.isLoading);

  const [seededSelectionsFor, setSeededSelectionsFor] = useState<string | undefined>(undefined);
  if (savedVariables.length && seededSelectionsFor !== issueId) {
    setSeededSelectionsFor(issueId);
    setSelections((prev) => {
      const next = {...prev};
      savedVariables.forEach((v) => {
        const variableId = Number(v.id);
        if (v.idValue != null && next[variableId] === undefined) {
          next[variableId] = Number(v.idValue);
        }
      });
      return next;
    });
  }

  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});
  const [groupsSeeded, setGroupsSeeded] = useState(false);
  if (groupsTree.length && !groupsSeeded) {
    setGroupsSeeded(true);
    setExpandedGroups((prev) => {
      const next = {...prev};
      groupsTree.forEach((g) => {
        if (next[Number(g.id)] === undefined) next[Number(g.id)] = true;
      });
      return next;
    });
  }

  const [saving, setSaving] = useState(false);

  const totalVariables = variableIds.length;
  const assessedVariables = useMemo(
    () => variableIds.filter((id) => selections[id] != null).length,
    [variableIds, selections]
  );


  const groupResults = useMemo(() => {
    const map = new Map<number, { values: number[]; level: number | null; matched: AssessmentOption | null }>();
    groupsTree.forEach((group) => {
      const groupId = Number(group.id);
      const values: number[] = [];
      (group.children ?? []).forEach((variable) => {
        const variableId = Number(variable.id);
        const selectedId = selections[variableId];
        if (selectedId == null) return;
        const opts = optionsByVariableId.get(variableId) ?? [];
        const opt = opts.find((o) => Number(o.id) === selectedId);
        if (!opt) return;
        values.push(numberOf(opt.numericValue));
      });
      // Match the group option whose formula holds; on ties keep the most severe.
      let matched: AssessmentOption | null = null;
      (groupOptionsByGroupId.get(groupId) ?? []).forEach((o) => {
        if (!evalFormula(o.formula, values)) return;
        if (!matched || numberOf(o.numericValue) > numberOf((matched as AssessmentOption).numericValue)) {
          matched = o;
        }
      });
      const level = matched ? numberOf((matched as AssessmentOption).numericValue) : null;
      map.set(groupId, {values, level, matched});
    });
    return map;
  }, [groupsTree, selections, optionsByVariableId, groupOptionsByGroupId]);

  const groupLevels = useMemo(
    () =>
      groupsTree
        .map((g) => groupResults.get(Number(g.id))?.level)
        .filter((v): v is number => v != null),
    [groupsTree, groupResults]
  );

  const finalScore = useMemo(
    () => (groupLevels.length ? Math.max(...groupLevels) : 0),
    [groupLevels]
  );

  const activeBand = useMemo(() => {
    const matches = scaleBands.filter((b) => b.formula && evalFormula(b.formula, groupLevels));
    if (matches.length) {
      return matches.reduce((a, b) => (numberOf(a.scoreMax) >= numberOf(b.scoreMax) ? a : b));
    }
    return matchBand(scaleBands, finalScore);
  }, [scaleBands, groupLevels, finalScore]);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    groupsTree.forEach((group) => {
      const groupId = Number(group.id);
      const expanded = expandedGroups[groupId] ?? true;
      const groupResult = groupResults.get(groupId);
      out.push({
        key: `g:${groupId}`,
        isGroup: true,
        group,
        worstOption: groupResult?.matched ?? null,
        expanded,
      });
      (group.children ?? []).forEach((variable) => {
        const variableId = Number(variable.id);
        out.push({
          key: `v:${groupId}:${variableId}`,
          isGroup: false,
          variable,
          options: optionsByVariableId.get(variableId) ?? [],
          groupId,
          hidden: !expanded,
          selectedOptionId: selections[variableId],
          optionsLoading: loadingByVariableId.get(variableId) ?? false,
        });
      });
    });
    return out;
  }, [groupsTree, expandedGroups, optionsByVariableId, loadingByVariableId, selections, groupResults]);

  const handleToggleGroup = (groupId: number) => {
    setExpandedGroups((prev) => ({...prev, [groupId]: !prev[groupId]}));
  };

  const handleVariableChange = (variableId: number, value: number | undefined) => {
    setSelections((prev) => ({...prev, [variableId]: value}));
  };

  const handleSave = async () => {
    if (!isEditMode || !issueId || issueCorrelative == null) {
      messageApi.warning(t('issues.impactAssessment.messages.saveError'));
      return;
    }

    const details: NonNullable<Parameters<typeof saveAssessment.mutateAsync>[0]>['details'] = [];
    const groupScorePayload: NonNullable<Parameters<typeof saveAssessment.mutateAsync>[0]>['groupScores'] = [];

    groupsTree.forEach((group) => {
      const groupId = Number(group.id);
      const groupLevel = groupResults.get(groupId)?.level ?? 0;
      const weightPct = numberOf(group.weightPct);
      groupScorePayload.push({
        groupId,
        groupScore: groupLevel,
        weightedScore: groupLevel * (weightPct / 100),
      });

      (group.children ?? []).forEach((variable) => {
        const variableId = Number(variable.id);
        const selectedId = selections[variableId];
        if (selectedId == null) return;
        const opts = optionsByVariableId.get(variableId) ?? [];
        const opt = opts.find((o) => Number(o.id) === selectedId);
        if (!opt) return;
        details.push({
          groupId,
          variableId,
          optionId: Number(opt.id),
          numericValue: numberOf(opt.numericValue),
          variableLabelSnap: variableLabelOf(variable),
          optionLabelSnap: opt.label,
          factorSnap: variable.factor != null ? Number(variable.factor) : null,
          weightSnap: weightPct,
        });
      });
    });

    const configGroup = groupsTree[0];

    setSaving(true);
    try {
      await saveAssessment.mutateAsync({
        version: {
          issueId: issueCorrelative,
          uid: issueId,
          configGroupId: configGroup ? Number(configGroup.id) : null,
          configVersion: configGroup?.configVersion ?? null,
          finalScore,
          scaleBandId: activeBand ? Number(activeBand.id) : null,
          status: activeBand?.label ?? null,
        },
        groupScores: groupScorePayload,
        details,
      });
      messageApi.success(t('issues.impactAssessment.messages.saveSuccess'));
    } catch (error) {
      console.error(error);
      messageApi.error(extractErrorMessage(error, t('issues.impactAssessment.messages.saveError')));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    onRegisterActions?.({
      submit: () => {
        void handleSave();
      }
    });
  }, [onRegisterActions, issueId, issueCorrelative, groupsTree, selections, optionsByVariableId, scaleBands, groupOptionsByGroupId, groupResults, finalScore, activeBand]);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  const columns: ColumnsType<Row> = [
    {
      title: t('issues.impactAssessment.columns.variable'),
      key: 'variable',
      width: '40%',
      render: (_v, row) => {
        if (row.isGroup) {
          const Icon = row.expanded ? MinusSquareOutlined : PlusSquareOutlined;
          return (
            <Space
              size={tokens.spacing.x8}
              className="gt-impact-group-label"
              onClick={() => handleToggleGroup(Number(row.group.id))}
              style={{cursor: 'pointer'}}
            >
              <Icon style={{color: tokens.colors.primary, fontSize: 16}}/>
              <strong>{labelOf(row.group)}</strong>
            </Space>
          );
        }
        return <span className="gt-impact-variable-label">{variableLabelOf(row.variable)}</span>;
      },
    },
    {
      title: t('issues.impactAssessment.columns.assessment'),
      key: 'assessment',
      width: '45%',
      render: (_v, row) => {
        if (row.isGroup) return null;
        const selected = row.options.find((o) => Number(o.id) === row.selectedOptionId);
        return (
          <Select<number>
            className="gt-impact-select"
            classNames={{popup: {root: 'gt-impact-select-popup'}}}
            style={{width: '100%'}}
            variant="filled"
            allowClear
            size="small"
            loading={row.optionsLoading}
            value={row.selectedOptionId}
            onChange={(val) => handleVariableChange(Number(row.variable.id), val)}
            onOpenChange={(open) => {
              if (open) handleOpenVariable(Number(row.variable.id));
            }}
            placeholder={t('issues.impactAssessment.fields.assessment.placeholder')}
            options={row.options.map((o) => ({
              value: Number(o.id),
              label: (
                <span style={{display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.x8}}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '1.4em',
                      flexShrink: 0,
                    }}
                  >
                    <SeverityDot color={optionDotColor(o, scaleBands)}/>
                  </span>
                  <span style={{whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4}}>
                    {o.label}
                  </span>
                </span>
              ),
            }))}
            optionLabelProp="label"
            labelRender={() =>
              selected ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: tokens.spacing.x8,
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                  title={selected.label}
                >
                  <SeverityDot color={optionDotColor(selected, scaleBands)}/>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {selected.label}
                  </span>
                </span>
              ) : null
            }
          />
        );
      },
    },
    {
      title: t('issues.impactAssessment.columns.level'),
      key: 'level',
      width: 170,
      align: 'right',
      render: (_v, row) => {
        if (!row.isGroup || !row.worstOption) return null;
        const color = optionDotColor(row.worstOption, scaleBands);
        const label = row.worstOption.scaleBandLabel || row.worstOption.label;
        return (
          <ScoreMeter
            size="md"
            value={numberOf(row.worstOption.numericValue)}
            max={scaleBands.length}
            color={color}
            label={label}
            count
          />
        );
      },
    },
  ];

  const isLoading = (isEditMode && isLoadingTree) || isLoadingActiveOptions || isLoadingGroupOptions;

  const reporter = issueData?.reportingUser;
  const reporterText = typeof reporter === 'string'
    ? reporter
    : (reporter?.name || reporter?.description || reporter?.id || undefined);
  const reportedOn = issueData?.dateReport
    ? dayjs(issueData.dateReport).toISOString()
    : undefined;

  return (
    <div className="gt-impact-assessment">
      {contextHolder}
      <Row gutter={[tokens.spacing.x24, tokens.spacing.x24]}>
        <Col xs={24} lg={16}>
          <Card
            className="gt-impact-card"
            title={
              <Space>
              <span className="gt-impact-card-title">
                {t('issues.impactAssessment.card.title')}
              </span>
                <span className="gt-impact-score-chip-count">
                  {assessedVariables} / {totalVariables}
                </span>
              </Space>
            }
            extra={
              <Space size={tokens.spacing.x12} className="gt-impact-score-chip">
                <span className="gt-impact-score-chip-label">
                  {t('issues.impactAssessment.card.generalAssessment')}
                </span>
                {activeBand && scaleBands.length ? (
                  <ScoreMeter
                    size="lg"
                    value={finalScore}
                    max={scaleBands.length}
                    color={activeBand.color}
                    label={activeBand.label}
                    count
                  />
                ) : null}
              </Space>
            }
          >
            <Skeleton loading={isLoading} active>
              <Table<Row>
                className="gt-impact-table"
                rowKey="key"
                size="small"
                tableLayout="fixed"
                columns={columns}
                dataSource={rows}
                pagination={false}
                showHeader
                rowClassName={(row) => {
                  if (row.isGroup) return 'gt-impact-row gt-impact-row--group';
                  if (row.hidden) return 'gt-impact-row gt-impact-row--hidden';
                  return 'gt-impact-row gt-impact-row--variable';
                }}
              />
            </Skeleton>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <IssueSummaryCard
            code={issueData?.codeIncident}
            statusLabel={issueData?.status?.description || issueData?.status?.name}
            statusColor={issueData?.status?.color || undefined}
            reportedBy={reporterText}
            reportedOn={reportedOn}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ImpactAssessment;
